import { Capacitor } from "@capacitor/core";
import { LocalNotifications } from "@capacitor/local-notifications";
import type { Reminder, Schedule } from "./types";

function numericId(id: string): number {
  let value = 0;
  for (let i = 0; i < id.length; i += 1) value = (value * 31 + id.charCodeAt(i)) >>> 0;
  return value % 2_000_000_000;
}

export async function scheduleAndroidReminders(reminders: Reminder[], mutedChannelIds: string[] = []) {
  return scheduleAndroidTimeline(reminders, [], mutedChannelIds);
}

export async function scheduleAndroidTimeline(reminders: Reminder[], schedules: Schedule[] = [], mutedChannelIds: string[] = []) {
  if (!Capacitor.isNativePlatform()) return { platform: "web", scheduled: 0 };
  const permission = await LocalNotifications.requestPermissions();
  if (permission.display !== "granted") return { platform: "native", scheduled: 0, permission: permission.display };
  const now = Date.now();
  const reminderNotifications = reminders
    .filter((item) => item.status === "scheduled" && new Date(item.remind_at).getTime() > now)
    .filter((item) => !item.channel_id || !mutedChannelIds.includes(item.channel_id))
    .map((item) => ({
      id: numericId(`reminder-${item.id}`),
      title: "MindBack 提醒",
      body: item.title,
      schedule: { at: new Date(item.remind_at), allowWhileIdle: true },
      extra: { reminderId: item.id, messageId: item.message_id || "" }
    }));
  const scheduleNotifications = schedules
    .filter((item) => item.status === "scheduled" && new Date(item.start_at).getTime() > now)
    .filter((item) => !item.channel_id || !mutedChannelIds.includes(item.channel_id))
    .map((item) => {
      const start = new Date(item.start_at).getTime();
      const lead = Math.max(0, item.remind_minutes_before || 0) * 60_000;
      const at = new Date(Math.max(Date.now() + 1000, start - lead));
      return {
        id: numericId(`schedule-${item.id}`),
        title: "MindBack 日程",
        body: item.title,
        schedule: { at, allowWhileIdle: true },
        extra: { scheduleId: item.id, messageId: item.message_id || "" }
      };
    });
  const notifications = [...reminderNotifications, ...scheduleNotifications].slice(0, 64);
  if (notifications.length > 0) await LocalNotifications.schedule({ notifications });
  return { platform: "native", scheduled: notifications.length, permission: permission.display };
}

export async function getNotificationStatus() {
  if (Capacitor.isNativePlatform()) {
    const permission = await LocalNotifications.checkPermissions();
    return { platform: "android", permission: permission.display, supported: true };
  }
  if ("Notification" in window) return { platform: "web", permission: Notification.permission, supported: true };
  return { platform: "web", permission: "unsupported", supported: false };
}

export async function requestWebNotificationPermission() {
  if ("Notification" in window && Notification.permission === "default") {
    return Notification.requestPermission();
  }
  const status = await getNotificationStatus();
  return status.permission;
}
